import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-note-main-content',
    templateUrl: './note-main-content.component.html',
    styleUrls: ['./note-main-content.component.scss'],
    standalone: false
})
export class NoteMainContentComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  // =============================================================================
  // INPUTS
  // =============================================================================

  @Input() mode!: 'create' | 'edit' | 'view';
  @Input() noteForm!: FormGroup;
  @Input() activeTab: number = 0;
  @Input() attachmentsCount: number = 0;
  @Input() commentsCount: number = 0;
  @Input() historyCount: number = 0;
  @Input() mentions: any[] = [];
  @Input() isMentionsLoading: boolean = false;
  @Input() hideDetailsCommentsHistory: boolean = false;

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  @Output() tabChange = new EventEmitter<number>();
  @Output() fieldChange = new EventEmitter<{field: string, value: any}>();
  @Output() editDescription = new EventEmitter<void>();
  @Output() mentionSelected = new EventEmitter<any>();
  @Output() mentionSearch = new EventEmitter<string>();

  // =============================================================================
  // VIEW CHILDREN
  // =============================================================================

  @ViewChild('descriptionTarget', { static: false }) descriptionTarget!: ElementRef;

  // =============================================================================
  // PRIVATE PROPERTIES
  // =============================================================================

  private destroy$ = new Subject<void>();

  // =============================================================================
  // PUBLIC PROPERTIES
  // =============================================================================

  public noteNameControl: FormControl;
  public noteDescriptionControl: FormControl;
  public activeTabIndex: number = 0;

  /**
   * Custom mention dropdown state
   */
  public showMentionDropdown: boolean = false;
  public mentionDropdownTop: number = 0;
  public mentionDropdownLeft: number = 0;
  public selectedMentionIndex: number = 0;
  private currentMentionSearch: string = '';
  private mentionStartPos: number = 0;
  private clickOutsideListener?: (event: MouseEvent) => void;

  // =============================================================================
  // LIFECYCLE HOOKS
  // =============================================================================

  constructor(private cdr: ChangeDetectorRef) {
    // Initialize form controls with validators
    this.noteNameControl = new FormControl('', [Validators.required]);
    this.noteDescriptionControl = new FormControl('');
  }

  ngOnInit(): void {
    this.syncFormControls();
    this.activeTabIndex = this.activeTab || 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['noteForm'] && !changes['noteForm'].firstChange) {
      this.syncFormControls();
    }

    if (changes['activeTab']) {
      this.activeTabIndex = this.activeTab || 0;
    }
  }

  ngAfterViewInit(): void {
    // Setup mention chip protection
    this.setupMentionChipProtection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeClickOutsideListener();
  }

  // =============================================================================
  // FORM SYNCHRONIZATION
  // =============================================================================

  /**
   * Synchronize local form controls with parent form group
   * Ensures two-way binding between component and parent
   */
  private syncFormControls(): void {
    if (!this.noteForm) {
      return;
    }

    // Sync note name
    const nameControl = this.noteForm.get('name');
    if (nameControl) {
      this.noteNameControl.setValue(nameControl.value, { emitEvent: false });

      // Subscribe to local changes -> update parent
      this.noteNameControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          nameControl.patchValue(value); // Emits events for real-time header updates
        });

      // Subscribe to parent changes -> update local
      nameControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.noteNameControl.setValue(value, { emitEvent: false });
        });
    }

    // Sync description
    const descriptionControl = this.noteForm.get('description');
    if (descriptionControl) {
      this.noteDescriptionControl.setValue(descriptionControl.value, {
        emitEvent: false,
      });

      // Render initial description content to DOM
      if (descriptionControl.value && this.descriptionTarget) {
        this.descriptionTarget.nativeElement.innerHTML =
          descriptionControl.value;
      }

      // Subscribe to local changes -> update parent
      this.noteDescriptionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          descriptionControl.patchValue(value); // Emits events for form synchronization
        });

      // Subscribe to parent changes -> update local and DOM
      descriptionControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.noteDescriptionControl.setValue(value, { emitEvent: false });
          // Update DOM when form control changes
          if (this.descriptionTarget) {
            this.descriptionTarget.nativeElement.innerHTML = value || '';
          }
        });
    }
  }

  // =============================================================================
  // TAB MANAGEMENT
  // =============================================================================

  public onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.tabChange.emit(index);
  }

  // =============================================================================
  // DESCRIPTION HANDLERS
  // =============================================================================

  /**
   * Handle description blur event
   * Extracts plain text and mentioned users, syncs to form
   */
  public onDescriptionChange(event: any): void {
    const htmlContent = event.target.innerHTML;

    // Extract plain text for FormControl
    const plainText = this.extractPlainText(htmlContent);
    this.noteDescriptionControl.setValue(plainText, { emitEvent: false });

    // Manually sync to parent form control to ensure save captures current value
    const descriptionControl = this.noteForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Extract mentioned user IDs
    this.extractMentionedUsers(htmlContent);
  }

  /**
   * Handle description input event (real-time updates)
   * Updates form control as user types
   */
  public onDescriptionInput(event: any): void {
    const htmlContent = event.target.innerHTML;
    const plainText = this.extractPlainText(htmlContent);
    this.noteDescriptionControl.setValue(plainText, { emitEvent: false });

    // Manually sync to parent form control to ensure save captures current value
    const descriptionControl = this.noteForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Extract and emit all mentioned users whenever content changes
    this.extractAndEmitMentionedUsers(htmlContent);
  }

  /**
   * Handle edit button click
   */
  public onEditDescription(): void {
    this.editDescription.emit();
  }

  // =============================================================================
  // CUSTOM MENTION HANDLERS
  // =============================================================================

  /**
   * Handle keyup events in description for custom @ mention detection
   * @param event Keyboard event
   */
  public onDescriptionKeyup(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const text = target.textContent || '';
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const cursorPos = this.getCaretPosition(target);

    // Check for @ symbol
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      // Check if there's a space after @, if so, close dropdown
      if (textAfterAt.includes(' ')) {
        this.closeMentionDropdown();
        return;
      }

      // Show dropdown with search query
      this.currentMentionSearch = textAfterAt;
      this.mentionStartPos = lastAtIndex;

      // Calculate dropdown position
      const rect = this.getCaretCoordinates();
      this.mentionDropdownTop = rect.top;
      this.mentionDropdownLeft = rect.left;

      this.showMentionDropdown = true;
      this.selectedMentionIndex = 0;

      // Setup click outside listener
      this.setupClickOutsideListener();

      // Trigger search
      this.mentionSearch.emit(textAfterAt);
    } else {
      this.closeMentionDropdown();
    }

    // Handle arrow keys and Enter in dropdown
    if (this.showMentionDropdown) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, this.mentions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.mentions[this.selectedMentionIndex]) {
          this.selectMention(this.mentions[this.selectedMentionIndex]);
        }
      } else if (event.key === 'Escape') {
        this.closeMentionDropdown();
      }
    }
  }

  /**
   * Get caret position in contenteditable div
   */
  private getCaretPosition(element: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  }

  /**
   * Get caret coordinates for dropdown positioning (viewport coordinates)
   */
  private getCaretCoordinates(): {top: number, left: number, height: number} {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return { top: 0, left: 0, height: 20 };
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Base viewport coordinates (for fixed positioning)
    let top = rect.bottom + window.scrollY; // Position below cursor
    let left = rect.left + window.scrollX;

    // -----------------------------------------------------------------------
    // Clamp dropdown horizontally so it never overflows off the right edge
    // -----------------------------------------------------------------------
    // This matches the behaviour in task and ticket description editors.
    const DROPDOWN_WIDTH = 280; // px – should roughly match .custom-mention-dropdown width
    const PADDING = 16;         // px – distance from viewport edge
    const viewportWidth = window.innerWidth;

    const maxLeft = viewportWidth - DROPDOWN_WIDTH - PADDING;
    if (left > maxLeft) {
      left = Math.max(PADDING, maxLeft);
    }

    return {
      top,
      left,
      height: rect.height || 20
    };
  }

  /**
   * Select a mention from dropdown
   * IMPROVED: Simple and reliable - preserves all existing mention chips
   */
  public selectMention(user: any): void {
    const target = this.descriptionTarget.nativeElement;
    const currentHTML = target.innerHTML;

    // Create the new mention chip HTML
    const mentionText = `@${user.full_name}`;
    const searchText = `@${this.currentMentionSearch}`;
    const mentionHTML = `<span class="e-mention-chip" data-mention-value="${user.id}" contenteditable="false">${mentionText}</span>`;

    // Simple approach: Find the search text in HTML and replace it
    // This preserves all existing chips because we're doing a targeted replacement

    // Get the position in the plain text
    const plainText = target.textContent || '';
    const searchStartPos = this.mentionStartPos;
    const searchEndPos = searchStartPos + searchText.length;

    // Build the replacement by working with innerHTML segments
    let newHTML = currentHTML;
    let replacementDone = false;

    // Method 1: Try direct replacement if search text exists in HTML
    if (currentHTML.includes(searchText)) {
      // Find the last occurrence (most recent typed)
      const lastIndex = currentHTML.lastIndexOf(searchText);

      // Make sure we're not replacing inside a chip
      const beforeSearchHTML = currentHTML.substring(0, lastIndex);
      const afterSearchHTML = currentHTML.substring(lastIndex + searchText.length);

      // Check if we're inside a chip by looking for unclosed chip tags
      const openChipCount = (beforeSearchHTML.match(/<span[^>]*class="[^"]*e-mention-chip[^"]*"/g) || []).length;
      const closeChipCount = (beforeSearchHTML.match(/<\/span>/g) || []).length;

      if (openChipCount === closeChipCount) {
        // We're not inside a chip, safe to replace
        newHTML = beforeSearchHTML + mentionHTML + '&nbsp;' + afterSearchHTML;
        replacementDone = true;
      }
    }

    // Method 2: If direct replacement didn't work, walk through the DOM
    if (!replacementDone) {
      newHTML = this.replaceSearchTextInHTML(currentHTML, searchText, mentionHTML, this.mentionStartPos);
    }

    // Set the new HTML
    target.innerHTML = newHTML;

    // Place cursor after the new mention
    setTimeout(() => {
      // Find the newly inserted chip and place cursor after it
      const allChips = target.querySelectorAll('.e-mention-chip');
      if (allChips.length > 0) {
        const lastChip = allChips[allChips.length - 1] as HTMLElement;
        this.moveCursorAfterElement(lastChip);
      }
    }, 10);

    // Extract and emit ALL mentioned users
    setTimeout(() => {
      this.extractAndEmitMentionedUsers(newHTML);

      // Update form control with plain text
      const newPlainText = this.extractPlainText(newHTML);
      this.noteDescriptionControl.setValue(newPlainText, { emitEvent: false });

      const descriptionControl = this.noteForm?.get('description');
      if (descriptionControl) {
        descriptionControl.setValue(newPlainText, { emitEvent: false });
      }
    }, 50);

    // Close dropdown
    this.closeMentionDropdown();
  }

  /**
   * Replace search text in HTML while preserving existing chips
   * Walks through the HTML character by character
   */
  private replaceSearchTextInHTML(html: string, searchText: string, replacement: string, targetPosition: number): string {
    // Parse HTML to work with DOM
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Walk through text nodes to find the target position
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
    let currentPos = 0;
    let targetNode: Text | null = null;
    let positionInNode = 0;

    let node;
    while (node = walker.nextNode()) {
      const textNode = node as Text;
      const text = textNode.textContent || '';
      const len = text.length;

      if (currentPos <= targetPosition && targetPosition < currentPos + len) {
        // Found the node containing our target position
        targetNode = textNode;
        positionInNode = targetPosition - currentPos;
        break;
      }

      currentPos += len;
    }

    if (targetNode) {
      const text = targetNode.textContent || '';
      const beforeText = text.substring(0, positionInNode);
      const afterText = text.substring(positionInNode + searchText.length);

      // Create replacement nodes
      const fragment = document.createDocumentFragment();

      if (beforeText) {
        fragment.appendChild(document.createTextNode(beforeText));
      }

      // Parse the replacement HTML
      const tempSpan = document.createElement('span');
      tempSpan.innerHTML = replacement + '&nbsp;';
      while (tempSpan.firstChild) {
        fragment.appendChild(tempSpan.firstChild);
      }

      if (afterText) {
        fragment.appendChild(document.createTextNode(afterText));
      }

      // Replace the text node
      targetNode.parentNode?.replaceChild(fragment, targetNode);
    }

    return tempDiv.innerHTML;
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Extract plain text from HTML content
   * Removes all HTML tags and returns clean text
   * @param html HTML string with mentions and formatting
   * @returns Plain text string
   */
  private extractPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Extract mentioned user IDs from HTML content
   * @param html HTML content with mention spans
   */
  private extractMentionedUsers(html: string): void {
    this.extractAndEmitMentionedUsers(html);
  }

  /**
   * Extract and emit ALL mentioned users from HTML content
   * This collects all mention chips and sends them to parent
   * @param html HTML content with mention spans
   */
  public extractAndEmitMentionedUsers(html: string): void {
    // Parse HTML to find all mention chips
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionChips = doc.querySelectorAll('.e-mention-chip');

    const mentionedUsers: any[] = [];
    const uniqueUserIds = new Set<number>();

    mentionChips.forEach((chip) => {
      const userId = chip.getAttribute('data-mention-value');
      const userName = chip.textContent?.replace('@', '').trim();

      if (userId) {
        const userIdNum = parseInt(userId, 10);
        // Only add if not already in set (avoid duplicates)
        if (!uniqueUserIds.has(userIdNum)) {
          uniqueUserIds.add(userIdNum);
          mentionedUsers.push({
            id: userIdNum,
            name: userName || '',
            full_name: userName || ''
          });
        }
      }
    });

    console.log('[CUSTOM MENTION] Extracted mentioned users:', mentionedUsers);

    // Always emit the full array of mentioned users (even if empty)
    this.mentionSelected.emit(mentionedUsers);
  }

  /**
   * Set description content with mentions (for initial load/view mode)
   * Converts plain text with mention references to styled HTML
   * @param text Plain text content
   * @param mentionedUsers Array of mentioned users
   */
  public setDescriptionContent(text: string, mentionedUsers: any[] = []): void {
    if (!this.descriptionTarget) return;

    // If text is empty or no users, just set it
    if (!text || !mentionedUsers || mentionedUsers.length === 0) {
      this.descriptionTarget.nativeElement.innerHTML = text || '';
      this.cdr.detectChanges();
      return;
    }

    let html = text || '';

    // Replace plain @mentions with styled spans
    // Process in reverse order to avoid replacing already replaced text
    mentionedUsers.forEach((user) => {
      const fullName = user.full_name || user.name;
      if (!fullName) return;

      const mentionText = `@${fullName}`;
      const mentionHtml = `<span class="e-mention-chip" data-mention-value="${user.id}" contenteditable="false">${mentionText}</span>`;
      
      // Escape special regex characters in the mention text
      const escapedMention = mentionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      // Use global replace to replace all occurrences
      html = html.replace(new RegExp(escapedMention, 'g'), mentionHtml);
    });

    this.descriptionTarget.nativeElement.innerHTML = html;
    this.cdr.detectChanges();
    
    // Immediately extract and emit all mentions after setting content
    // This ensures existing mentions are captured
    setTimeout(() => {
      this.extractAndEmitMentionedUsers(html);
    }, 50);
  }

  /**
   * Setup click outside listener to close dropdown
   */
  private setupClickOutsideListener(): void {
    // Remove existing listener if any
    this.removeClickOutsideListener();

    // Add new listener after a small delay to avoid immediate closure
    setTimeout(() => {
      this.clickOutsideListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const dropdownEl = document.querySelector('.custom-mention-dropdown');
        const descriptionEl = this.descriptionTarget?.nativeElement;

        // Check if click is outside both dropdown and description field
        if (dropdownEl && !dropdownEl.contains(target) &&
            descriptionEl && !descriptionEl.contains(target)) {
          this.closeMentionDropdown();
          this.cdr.detectChanges();
        }
      };

      document.addEventListener('click', this.clickOutsideListener as any);
    }, 100);
  }

  /**
   * Remove click outside listener
   */
  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener as any);
      this.clickOutsideListener = undefined;
    }
  }

  /**
   * Close mention dropdown
   */
  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.currentMentionSearch = '';
    this.selectedMentionIndex = 0;
    this.removeClickOutsideListener();
  }

  /**
   * Setup protection to prevent cursor from entering mention chips
   * Moves cursor outside chip when user tries to edit inside it
   */
  private setupMentionChipProtection(): void {
    setTimeout(() => {
      if (!this.descriptionTarget) return;

      const target = this.descriptionTarget.nativeElement;

      // Handle click events on chips
      target.addEventListener('click', (event: MouseEvent) => {
        const clickedElement = event.target as HTMLElement;

        // Check if clicked on a mention chip
        if (clickedElement.classList.contains('e-mention-chip') ||
            clickedElement.closest('.e-mention-chip')) {
          const chip = clickedElement.classList.contains('e-mention-chip')
            ? clickedElement
            : clickedElement.closest('.e-mention-chip');

          if (chip) {
            // Move cursor after the chip
            this.moveCursorAfterElement(chip as HTMLElement);
            event.preventDefault();
            event.stopPropagation();
          }
        }
      });

      // Handle keydown to prevent editing inside chips
      target.addEventListener('keydown', (event: KeyboardEvent) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let node = range.startContainer;

        // Check if cursor is inside a chip
        while (node && node !== target) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains('e-mention-chip')) {
              // Cursor is inside chip, move it outside
              if (event.key === 'Backspace' || event.key === 'Delete') {
                // Allow deletion of entire chip
                element.remove();
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowLeft') {
                this.moveCursorBeforeElement(element);
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowRight') {
                this.moveCursorAfterElement(element);
                event.preventDefault();
                return;
              } else {
                // For other keys, move cursor after chip
                this.moveCursorAfterElement(element);
                return; // Let the key be processed after cursor move
              }
            }
          }
          node = node.parentNode as Node;
        }
      });
    }, 100);
  }

  /**
   * Move cursor before an element
   */
  private moveCursorBeforeElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartBefore(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /**
   * Move cursor after an element
   */
  private moveCursorAfterElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartAfter(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);

    // Insert a zero-width space to ensure cursor has somewhere to go
    const textNode = document.createTextNode('\u200B');
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}
